// src/components/policies/PolicyViewer.tsx
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';

const client = generateClient<Schema>();

interface PolicyValue {
    value?: any;
    policySchema?: string;
    sourceKey?: string;
}

interface ResolvedPolicy {
    targetKey?: any;
    value?: PolicyValue;
    sourceKey?: any;
    addedSourceKey?: any;
}

interface BestPractice {
    id: string;
    policySchema: string;
    recommendedValue: any;
    notes: string | null;
}

export default function PolicyViewer() {
    const queryClient = useQueryClient();
    const [selectedTenantId, setSelectedTenantId] = useState<string>('');
    const [orgUnitPath, setOrgUnitPath] = useState('/');
    const [policyType, setPolicyType] = useState<'device' | 'user'>('device');
    const [policies, setPolicies] = useState<any>(null);
    const [debugInfo, setDebugInfo] = useState<any>(null);
    const [schemaDescriptions, setSchemaDescriptions] = useState<Record<string, string>>({});
    const [loadingPolicies, setLoadingPolicies] = useState(false);
    const [debugExpanded, setDebugExpanded] = useState(false);
    const [editingSchema, setEditingSchema] = useState<string | null>(null);
    const [editValue, setEditValue] = useState('');
    const [editNotes, setEditNotes] = useState('');
    const [savingBestPractice, setSavingBestPractice] = useState(false);

    // Fetch all tenants
    const { data: tenants, isLoading: tenantsLoading } = useQuery({
        queryKey: ['tenants'],
        queryFn: async () => {
            const { data } = await client.models.Tenant.list();
            return data;
        },
    });

    // Fetch org units when a tenant is selected
    const { data: orgUnits, isLoading: orgUnitsLoading } = useQuery({
        queryKey: ['orgUnits', selectedTenantId],
        queryFn: async () => {
            const result = await client.queries.fetchOrgUnits({
                tenantId: selectedTenantId,
            });
            const data = typeof result.data === 'string'
                ? JSON.parse(result.data)
                : result.data;
            if (data?.success) {
                return data.orgUnits as { id: string; name: string; path: string; parentPath: string }[];
            }
            return [];
        },
        enabled: !!selectedTenantId,
    });

    // Fetch best practice policies
    const { data: bestPractices, isLoading: bestPracticesLoading } = useQuery({
        queryKey: ['bestPractices'],
        queryFn: async () => {
            const { data } = await client.models.BestPracticePolicy.list();
            return data as BestPractice[];
        },
    });

    // Build lookup map: policySchema -> best practice
    const bestPracticeMap = useMemo(() => {
        const map: Record<string, BestPractice> = {};
        if (bestPractices) {
            for (const bp of bestPractices) {
                map[bp.policySchema] = bp;
            }
        }
        return map;
    }, [bestPractices]);

    // Fetch policies for selected tenant
    const fetchPoliciesMutation = useMutation({
        mutationFn: async () => {
            if (!selectedTenantId) {
                throw new Error('Please select a tenant');
            }

            setLoadingPolicies(true);
            const result = await client.queries.fetchTenantPolicies({
                tenantId: selectedTenantId,
                orgUnitPath: orgUnitPath,
                policyType: policyType,
            });

            const data = typeof result.data === 'string'
                ? JSON.parse(result.data)
                : result.data;

            return data;
        },
        onSuccess: (data) => {
            console.log('Fetched policies:', data);

            if (data.debug) {
                setDebugInfo(data.debug);
                console.log('Debug info:', data.debug);
            }

            if (data.schemaDescriptions) {
                setSchemaDescriptions(data.schemaDescriptions);
            }

            if (data.success) {
                setPolicies(data.policies || {});
            } else {
                alert(`Failed to fetch policies: ${data.error}\n\nDebug: ${JSON.stringify(data.debug, null, 2)}`);
            }

            setLoadingPolicies(false);
        },
        onError: (error: any) => {
            console.error('Failed to fetch policies:', error);
            alert(`Failed to fetch policies: ${error.message}`);
            setLoadingPolicies(false);
        },
    });

    const handleFetchPolicies = () => {
        fetchPoliciesMutation.mutate();
    };

    // Format policy schema name for display
    const formatPolicyName = (schemaName: string) => {
        const parts = schemaName.split('.');
        return parts[parts.length - 1]
            .replace(/([A-Z])/g, ' $1')
            .trim();
    };

    // Render policy value in human-readable format
    const renderPolicyValue = (value: any): string => {
        if (value === null || value === undefined) {
            return 'Not set';
        }
        if (typeof value === 'boolean') {
            return value ? 'Enabled' : 'Disabled';
        }
        if (typeof value === 'object') {
            if (Array.isArray(value)) {
                return value.join(', ');
            }
            return JSON.stringify(value, null, 2);
        }
        return String(value);
    };

    // Compare tenant value against best practice
    const valuesMatch = (tenantValue: any, recommendedValue: any): boolean => {
        return JSON.stringify(tenantValue) === JSON.stringify(recommendedValue);
    };

    // Start editing a best practice
    const startEditing = (schemaName: string) => {
        const existing = bestPracticeMap[schemaName];
        if (existing) {
            setEditValue(typeof existing.recommendedValue === 'object'
                ? JSON.stringify(existing.recommendedValue, null, 2)
                : String(existing.recommendedValue));
            setEditNotes(existing.notes || '');
        } else {
            setEditValue('');
            setEditNotes('');
        }
        setEditingSchema(schemaName);
    };

    // Save best practice
    const saveBestPractice = async (schemaName: string) => {
        setSavingBestPractice(true);
        try {
            let parsedValue: any;
            try {
                parsedValue = JSON.parse(editValue);
            } catch {
                parsedValue = editValue;
            }

            const existing = bestPracticeMap[schemaName];
            const bpPolicyType = policyType === 'device' ? 'CHROME_DEVICE' : 'CHROME_USER';

            if (existing) {
                await client.models.BestPracticePolicy.update({
                    id: existing.id,
                    recommendedValue: parsedValue,
                    notes: editNotes || null,
                    updatedAt: new Date().toISOString(),
                });
            } else {
                await client.models.BestPracticePolicy.create({
                    policySchema: schemaName,
                    policyType: bpPolicyType,
                    recommendedValue: parsedValue,
                    notes: editNotes || null,
                    updatedAt: new Date().toISOString(),
                });
            }

            queryClient.invalidateQueries({ queryKey: ['bestPractices'] });
            setEditingSchema(null);
        } catch (error: any) {
            console.error('Failed to save best practice:', error);
            alert(`Failed to save: ${error.message}`);
        } finally {
            setSavingBestPractice(false);
        }
    };

    // Delete best practice
    const deleteBestPractice = async (schemaName: string) => {
        const existing = bestPracticeMap[schemaName];
        if (!existing) return;

        try {
            await client.models.BestPracticePolicy.delete({ id: existing.id });
            queryClient.invalidateQueries({ queryKey: ['bestPractices'] });
            setEditingSchema(null);
        } catch (error: any) {
            console.error('Failed to delete best practice:', error);
            alert(`Failed to delete: ${error.message}`);
        }
    };

    // Compute compliance stats
    const complianceStats = useMemo(() => {
        if (!policies) return null;

        let totalWithBestPractice = 0;
        let matching = 0;

        for (const [schemaName, resolvedPolicies] of Object.entries(policies)) {
            const bp = bestPracticeMap[schemaName];
            if (!bp) continue;

            totalWithBestPractice++;
            const policyArray = Array.isArray(resolvedPolicies) ? resolvedPolicies : [resolvedPolicies];
            const tenantValue = policyArray[0]?.value?.value;

            if (tenantValue !== undefined && valuesMatch(tenantValue, bp.recommendedValue)) {
                matching++;
            }
        }

        return { totalWithBestPractice, matching };
    }, [policies, bestPracticeMap]);

    return (
        <div className="space-y-6">
            {/* Header and Controls */}
            <div className="bg-white shadow-md rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold text-gray-800">
                        Chrome {policyType === 'device' ? 'Device' : 'User'} Policies
                    </h2>

                    {/* Device / User Toggle */}
                    <div className="inline-flex rounded-md shadow-sm" role="group">
                        <button
                            onClick={() => { setPolicyType('device'); setPolicies(null); }}
                            className={`px-4 py-2 text-sm font-medium rounded-l-md border ${
                                policyType === 'device'
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            Device
                        </button>
                        <button
                            onClick={() => { setPolicyType('user'); setPolicies(null); }}
                            className={`px-4 py-2 text-sm font-medium rounded-r-md border-t border-b border-r ${
                                policyType === 'user'
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            User
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Tenant Selector */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Tenant
                        </label>
                        <select
                            value={selectedTenantId}
                            onChange={(e) => { setSelectedTenantId(e.target.value); setOrgUnitPath('/'); }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={tenantsLoading}
                        >
                            <option value="">-- Select a tenant --</option>
                            {tenants?.map((tenant) => (
                                <option key={tenant.id} value={tenant.id}>
                                    {tenant.name} ({tenant.domain})
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Org Unit Selector */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Organizational Unit
                        </label>
                        <select
                            value={orgUnitPath}
                            onChange={(e) => setOrgUnitPath(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={!selectedTenantId || orgUnitsLoading}
                        >
                            {!selectedTenantId && (
                                <option value="/">-- Select a tenant first --</option>
                            )}
                            {selectedTenantId && orgUnitsLoading && (
                                <option value="/">Loading org units...</option>
                            )}
                            {selectedTenantId && !orgUnitsLoading && orgUnits?.map((ou) => (
                                <option key={ou.path} value={ou.path}>
                                    {ou.path === '/' ? `/ (${ou.name})` : `${ou.path}`}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Fetch Button */}
                    <div className="flex items-end">
                        <button
                            onClick={handleFetchPolicies}
                            disabled={!selectedTenantId || loadingPolicies}
                            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loadingPolicies ? 'Loading...' : 'Fetch Policies'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Debug Info (collapsible) */}
            {debugInfo && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg">
                    <button
                        onClick={() => setDebugExpanded(!debugExpanded)}
                        className="w-full flex items-center justify-between p-4 text-left"
                    >
                        <h4 className="text-sm font-semibold text-yellow-800">Debug Info</h4>
                        <svg
                            className={`h-4 w-4 text-yellow-800 transition-transform ${debugExpanded ? 'rotate-180' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    {debugExpanded && (
                        <pre className="text-xs text-yellow-900 whitespace-pre-wrap px-4 pb-4">
                            {JSON.stringify(debugInfo, null, 2)}
                        </pre>
                    )}
                </div>
            )}

            {/* Policies Display */}
            {policies && (
                <div className="bg-white shadow-md rounded-lg overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800">
                                    Policy Settings
                                </h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    {Object.keys(policies).length} policy schemas found
                                </p>
                            </div>

                            {/* Compliance Summary */}
                            {complianceStats && complianceStats.totalWithBestPractice > 0 && (
                                <div className="text-right">
                                    <div className="text-sm font-medium text-gray-700">
                                        Compliance: {complianceStats.matching} / {complianceStats.totalWithBestPractice} match best practice
                                    </div>
                                    <div className="w-48 bg-gray-200 rounded-full h-2 mt-1">
                                        <div
                                            className={`h-2 rounded-full ${
                                                complianceStats.matching === complianceStats.totalWithBestPractice
                                                    ? 'bg-green-500'
                                                    : complianceStats.matching / complianceStats.totalWithBestPractice >= 0.7
                                                        ? 'bg-yellow-500'
                                                        : 'bg-red-500'
                                            }`}
                                            style={{ width: `${(complianceStats.matching / complianceStats.totalWithBestPractice) * 100}%` }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Column Headers */}
                        <div className="grid grid-cols-2 gap-6 mt-4 pt-3 border-t border-gray-200">
                            <div className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                                Current Tenant Setting
                            </div>
                            <div className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                                Best Practice
                            </div>
                        </div>
                    </div>

                    <div className="divide-y divide-gray-200">
                        {Object.entries(policies).map(([schemaName, resolvedPolicies]: [string, any]) => {
                            const policyArray = Array.isArray(resolvedPolicies)
                                ? resolvedPolicies
                                : [resolvedPolicies];

                            const bp = bestPracticeMap[schemaName];
                            const tenantValue = policyArray[0]?.value?.value;
                            const hasBestPractice = !!bp;
                            const isMatch = hasBestPractice && tenantValue !== undefined && valuesMatch(tenantValue, bp.recommendedValue);
                            const isMismatch = hasBestPractice && tenantValue !== undefined && !isMatch;
                            const isEditing = editingSchema === schemaName;

                            return (
                                <div key={schemaName} className="p-6 hover:bg-gray-50">
                                    {/* Policy name and description span full width */}
                                    <h4 className="text-md font-semibold text-gray-900 mb-1">
                                        {formatPolicyName(schemaName)}
                                    </h4>
                                    <p className="text-xs text-gray-500 font-mono">
                                        {schemaName}
                                    </p>
                                    {schemaDescriptions[schemaName] && (
                                        <p className="text-sm text-gray-600 mt-1 mb-3">
                                            {schemaDescriptions[schemaName]}
                                        </p>
                                    )}
                                    {!schemaDescriptions[schemaName] && <div className="mb-3" />}

                                    {/* Two-column layout */}
                                    <div className="grid grid-cols-2 gap-6">
                                        {/* Left Column: Tenant Setting */}
                                        <div>
                                            {policyArray.map((policy: ResolvedPolicy, idx: number) => (
                                                <div key={idx}>
                                                    {policy.value && (
                                                        <div className={`rounded-md p-3 border ${
                                                            isMatch
                                                                ? 'bg-green-50 border-green-200'
                                                                : isMismatch
                                                                    ? 'bg-red-50 border-red-200'
                                                                    : 'bg-gray-100 border-gray-200'
                                                        }`}>
                                                            <div className="text-sm">
                                                                <span className="font-medium text-gray-700">Value: </span>
                                                                <span className={`${
                                                                    isMatch ? 'text-green-900' : isMismatch ? 'text-red-900' : 'text-gray-900'
                                                                }`}>
                                                                    {renderPolicyValue(policy.value.value)}
                                                                </span>
                                                            </div>
                                                            {isMatch && (
                                                                <div className="text-xs text-green-700 mt-1 font-medium">
                                                                    Matches best practice
                                                                </div>
                                                            )}
                                                            {isMismatch && (
                                                                <div className="text-xs text-red-700 mt-1 font-medium">
                                                                    Does not match best practice
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}

                                            {policyArray.length === 0 && (
                                                <div className="text-sm text-gray-500 italic">
                                                    No value set (inherited or default)
                                                </div>
                                            )}
                                        </div>

                                        {/* Right Column: Best Practice */}
                                        <div>
                                            {isEditing ? (
                                                /* Inline Editor */
                                                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 space-y-3">
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                                            Recommended Value
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={editValue}
                                                            onChange={(e) => setEditValue(e.target.value)}
                                                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                            placeholder="e.g., true, 2, or JSON"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                                            Notes
                                                        </label>
                                                        <textarea
                                                            value={editNotes}
                                                            onChange={(e) => setEditNotes(e.target.value)}
                                                            rows={3}
                                                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                            placeholder="Why is this the recommended setting?"
                                                        />
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => saveBestPractice(schemaName)}
                                                            disabled={savingBestPractice || !editValue.trim()}
                                                            className="px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                                        >
                                                            {savingBestPractice ? 'Saving...' : 'Save'}
                                                        </button>
                                                        <button
                                                            onClick={() => setEditingSchema(null)}
                                                            className="px-3 py-1 text-xs font-medium bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                                        >
                                                            Cancel
                                                        </button>
                                                        {bp && (
                                                            <button
                                                                onClick={() => deleteBestPractice(schemaName)}
                                                                className="px-3 py-1 text-xs font-medium bg-red-100 text-red-700 rounded hover:bg-red-200 ml-auto"
                                                            >
                                                                Remove
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : bp ? (
                                                /* Display existing best practice */
                                                <div className="bg-gray-100 border border-gray-200 rounded-md p-3">
                                                    <div className="text-sm">
                                                        <span className="font-medium text-gray-700">Recommended: </span>
                                                        <span className="text-gray-900">
                                                            {renderPolicyValue(bp.recommendedValue)}
                                                        </span>
                                                    </div>
                                                    {bp.notes && (
                                                        <div className="text-sm text-gray-600 mt-2 italic">
                                                            {bp.notes}
                                                        </div>
                                                    )}
                                                    <button
                                                        onClick={() => startEditing(schemaName)}
                                                        className="mt-2 px-3 py-1 text-xs font-medium bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                                    >
                                                        Edit
                                                    </button>
                                                </div>
                                            ) : (
                                                /* No best practice set */
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm text-gray-400 italic">
                                                        No best practice set
                                                    </span>
                                                    <button
                                                        onClick={() => startEditing(schemaName)}
                                                        className="px-3 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                                    >
                                                        Set Best Practice
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loadingPolicies && !policies && (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            )}

            {/* Empty State */}
            {!policies && !loadingPolicies && (
                <div className="bg-white shadow-md rounded-lg p-12 text-center">
                    <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No policies loaded</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        Select a tenant and click "Fetch Policies" to view Chrome device policies
                    </p>
                </div>
            )}
        </div>
    );
}
